import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';

import type { CurriculumVitae } from '../../types/curriculum-vitae';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { CategoryBadge } from './card/category-badge';
import { RiMapPin2Line } from 'react-icons/ri';
import { AddToFavourite } from './card/add-to-favorites';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { FiUser } from 'react-icons/fi';
import { BiLock } from 'react-icons/bi';

export const CurriculumVitaeCard = ({ curriculum }: { curriculum: CurriculumVitae }) => {
  const me = useCustomerStore(s => s.me);

  const showFavoriteButton = me ? true : false;

  const topped =
    curriculum?.topped_up_to && new Date(curriculum.topped_up_to).getTime() > Date.now();
  const toppedType = curriculum?.metadata?.top_type;
  const hasVideo = !!curriculum?.video?.url;

  const isPurchased = curriculum?.purchased;
  const imageUrl = isPurchased ? curriculum?.image?.url : null;
  const curriculumHref = curriculum.slug ? `/${curriculum.slug.replace(/^\/+/, '')}` : '#';

  return (
    <div
      className={cn(
        'relative col-span-1 flex h-full w-full cursor-pointer overflow-hidden rounded-[24px]',
        'curriculum-vitae-card__shadow',
      )}
    >
      {showFavoriteButton && (
        <AddToFavourite
          curriculum={curriculum}
          type="curriculum"
          className={{ container: 'absolute right-6 top-6 z-[10] h-9 w-9' }}
        />
      )}
      <a
        href={curriculumHref}
        className="curriculum-vitae-card__shadow col-span-1 h-full w-full flex-grow overflow-hidden rounded-[24px]"
      >
        <div className="relative h-[200px] w-full overflow-y-clip sm:h-[230px]">
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt={resolveImageAlt((isPurchased ? curriculum?.image : undefined), curriculum?.name ?? 'User name')}
              fill
              style={{ objectFit: 'cover', objectPosition: 'center' }}
              sizes="(min-width: 640px) 50vw, 100vw"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-primary/30">
              <FiUser className="h-full w-full" />
            </div>
          )}
          {!isPurchased && (
            <div
              className="
        /* 
        place at    the midpoint */ /* shift back
        by 
        half its  own size */ absolute left-1/2 top-1/2 flex h-20 w-20
        -translate-x-1/2 -translate-y-1/2 transform items-center justify-center 
        rounded-full bg-white opacity-40
      "
            >
              <BiLock className="z-20 h-16 w-16 text-muted-foreground" />
            </div>
          )}
          {hasVideo && (
            <div className="absolute -right-10 bottom-7 flex h-[30px] -rotate-45 items-center  justify-center bg-primary px-8 text-xs font-bold text-white">
              VIDEOŽIVOTOPIS
            </div>
          )}
        </div>
        {topped && (
          <div className="absolute left-0 top-0 z-[10] flex h-[30px] w-auto items-center justify-center rounded-br-lg bg-primary px-8 text-sm font-bold text-white">
            {toppedType ?? 'I AM FREE'}
          </div>
        )}
        <div className="flex h-full w-full flex-grow flex-col items-start justify-start gap-2 bg-white p-6">
          <div className="flex h-[22px] w-full flex-wrap items-start justify-start gap-2 overflow-hidden md:h-[33px]">
            {curriculum?.categories?.map((category: { id: string; name: string }) => (
              <CategoryBadge
                key={category?.name}
                category={{
                  name: category?.name,
                  id: category?.id?.toString(),
                  slug: category?.name?.toLowerCase().replace(/\s+/g, '-') || '',
                }}
                asLink={false}
              />
            ))}
          </div>
          {isPurchased && (
            <h4 className="text-xl font-bold text-foreground">
              {curriculum.name} {curriculum.surname}
            </h4>
          )}
          {!isPurchased && <h4 className="text-xl font-bold text-foreground">{curriculum.name}</h4>}
          {curriculum.city && (
            <div className="flex gap-2 text-sm text-primary md:text-base">
              <RiMapPin2Line className="h-4 w-4 md:h-5 md:w-5" />
              <span>
                {curriculum.city}
                {curriculum.country?.id ? `, ${curriculum.country.id}` : ''}
              </span>
            </div>
          )}
          {!curriculum.city && curriculum.country?.name && (
            <div className="flex gap-2 text-sm text-primary md:text-base">
              <RiMapPin2Line className="h-4 w-4 md:h-5 md:w-5" />
              <span>{curriculum.country.name}</span>
            </div>
          )}
        </div>
      </a>
    </div>
  );
};
